import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession } from '@/store/useSession'
import { useTheme } from '@/store/useTheme'
import { PALETTES } from '@/store/palettes'
import styles from '@/styles/sessionFlow.module.css'

function PaletteTick() {
  return (
    <svg
      className={styles.paletteTick}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="2 6 5 9 10 3" />
    </svg>
  )
}

function ChoiceCard({
  label,
  icon,
  selected,
  disabled,
  locked,
}: {
  label: string
  icon?: string
  selected: boolean
  disabled?: boolean
  locked?: boolean
}) {
  return (
    <button
      type="button"
      className={`${styles.card} ${disabled ? styles.cardDisabled : ''} ${locked ? styles.cardLocked : ''}`}
      aria-pressed={selected}
      disabled={disabled || locked}
      aria-disabled={disabled || locked}
      tabIndex={locked ? -1 : undefined}
    >
      <span className={styles.dot} aria-hidden={disabled}>
        <span className={styles.dotFill} />
      </span>
      {icon && <span className={styles.cardIcon} aria-hidden="true">{icon}</span>}
      <span className={styles.cardLabel}>{label}</span>
    </button>
  )
}

export default function ConsentPage() {
  const navigate = useNavigate()
  const { practitioner, setShared } = useSession()
  const { palette, setPalette } = useTheme()

  const [paletteChosen, setPaletteChosen] = useState(false)

  const isUnder18Session = practitioner.relationshipType === 'parent-child'

  useEffect(() => {
    setShared({ captureMethod: 'canvas' })
  }, [setShared])

  const handleContinue = () => {
    setShared({ captureMethod: 'canvas' })
    navigate('/session/start')
  }

  return (
    <main className={styles.page}>
      <div className={styles.inner}>

        {/* ── Capture method ───────────────────────────────────────── */}
        <section className={styles.section} aria-labelledby="q-capture">
          <p id="q-capture" className={styles.bodyText}>
            Today you'll be working together on a shared canvas — a digital
            space where you can both write, draw and map what comes up.
          </p>

          <div className={styles.cardRow} role="group" aria-labelledby="q-capture">
            <ChoiceCard
              label="Shared canvas"
              icon="🎨"
              selected
              locked
            />
            <ChoiceCard
              label="Audio (coming soon)"
              icon="🎙"
              selected={false}
              disabled
            />
          </div>

          <p className={styles.quietNoteItalic}>
            Audio recording is currently in development. When available, full
            information about storage, consent and data handling will be provided
            here before you choose. Recording a session has implications for
            consent, data storage and professional standards — these will be
            clearly explained.
          </p>

          {isUnder18Session && (
            <p className={styles.notice} role="note">
              If anyone in this session is under 18, a parent or guardian will
              need to agree to this on their behalf.
            </p>
          )}
        </section>

        <hr className={styles.divider} />

        {/* ── Palette ──────────────────────────────────────────────── */}
        <section className={styles.section} aria-labelledby="q-palette">
          <h2 id="q-palette" className={styles.question}>
            First, what colours would you like as a backdrop?
          </h2>

          <div className={styles.paletteRow} role="group" aria-labelledby="q-palette">
            {PALETTES.map(p => {
              const isSelected = palette.id === p.id && paletteChosen
              return (
                <button
                  key={p.id}
                  type="button"
                  className={styles.paletteCircle}
                  data-selected={isSelected}
                  aria-pressed={isSelected}
                  aria-label={`${p.label}${isSelected ? ' – selected' : ''}`}
                  title={p.label}
                  style={{ backgroundColor: p.swatch, color: p.text }}
                  onClick={() => {
                    setPalette(p.id)
                    setPaletteChosen(true)
                  }}
                >
                  {isSelected && <PaletteTick />}
                </button>
              )
            })}
          </div>
        </section>

      </div>

      {/* ── Continue ─────────────────────────────────────────────────── */}
      <footer className={styles.footer}>
        <button
          type="button"
          className={styles.continueBtn}
          disabled={!paletteChosen}
          aria-disabled={!paletteChosen}
          onClick={handleContinue}
        >
          {paletteChosen ? 'Continue' : 'Choose a colour above'}
        </button>
      </footer>
    </main>
  )
}
